import { useNavigate } from 'react-router-dom';

interface HeroContent {
  tag?: string;
  titleLine1: string;
  titleHighlight: string;
  titleLine2?: string;
  subtitle: string;
  ctaPrimary: string;
  ctaPrimaryHref: string;
  ctaSecondary: string;
  ctaSecondaryHref: string;
  bgImage: string;
  stats?: Array<{ value: string; label: string }>;
}

interface HeroSectionProps {
  content?: HeroContent;
}

const defaultContent: HeroContent = {
  tag: 'Over 1,000 Blind Parts In Stock — Fast Nationwide Shipping Since 1997',
  titleLine1: 'Shop Blind Parts',
  titleHighlight: '& Window Hardware',
  titleLine2: 'for Every Brand & Model',
  subtitle:
    'Thousands of replacement parts for vertical blinds, mini blinds, roller shades, and drapery hardware. Find the exact part you need and get it shipped nationwide — fast.',
  ctaPrimary: 'Shop Now',
  ctaPrimaryHref: '/category/vertical-blind-parts',
  ctaSecondary: 'Find Your Part',
  ctaSecondaryHref: '#categories',
  bgImage:
    'https://public.readdy.ai/ai/img_res/e02bb5a601d9760885e8f45376d08fc4.jpg',
  stats: [
    { value: '1,000+', label: 'Parts In Stock' },
    { value: 'Since 1997', label: 'In Business' },
    { value: '1–2 Days', label: 'Ships In' },
    { value: '4.9★', label: 'Customer Rating' },
  ],
};

export default function HeroSection({ content = defaultContent }: HeroSectionProps) {
  const navigate = useNavigate();

  const handleNav = (href: string) => {
    if (href.startsWith('#')) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(href);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={content.bgImage}
          alt="Blind parts and window hardware store"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-stone-950/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/85 to-stone-950/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-transparent to-stone-950/30" />
      </div>

      {/* Content */}
      <div className="relative w-full max-w-screen-2xl mx-auto px-4 lg:px-8 pt-48 pb-20">
        <div className="max-w-2xl">
          {content.tag && (
            <div className="inline-flex items-center gap-2 bg-amber-600/20 border border-amber-500/40 text-amber-300 text-[11px] font-bold tracking-widest uppercase px-4 py-2 rounded-sm mb-7">
              <span className="w-1.5 h-1.5 bg-amber-400 rounded-full inline-block flex-shrink-0" />
              {content.tag}
            </div>
          )}

          <h1 className="font-extrabold text-white leading-[1.06] tracking-tight mb-6" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.8)' }}>
            <span className="block text-5xl lg:text-6xl xl:text-7xl">{content.titleLine1}</span>
            <span className="block text-5xl lg:text-6xl xl:text-7xl text-amber-400">
              {content.titleHighlight}
            </span>
            {content.titleLine2 && (
              <span className="block text-3xl lg:text-5xl text-stone-300 mt-2 font-bold">
                {content.titleLine2}
              </span>
            )}
          </h1>

          <p className="text-stone-200 text-lg leading-relaxed mb-10 max-w-xl" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}>
            {content.subtitle}
          </p>

          <div className="flex flex-wrap gap-4 mb-14">
            <button
              onClick={() => handleNav(content.ctaPrimaryHref)}
              className="bg-amber-600 hover:bg-amber-500 text-white font-extrabold text-sm tracking-wide px-8 py-4 rounded transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2.5"
            >
              <i className="ri-store-2-line text-base" />
              {content.ctaPrimary}
            </button>
            <button
              onClick={() => handleNav(content.ctaSecondaryHref)}
              className="bg-white/10 hover:bg-white/20 border border-white/40 text-white font-semibold text-sm tracking-wide px-8 py-4 rounded transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-search-eye-line text-base" />
              {content.ctaSecondary}
            </button>
          </div>

          {/* Stats */}
          {content.stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 rounded overflow-hidden max-w-xl">
              {content.stats.map((s) => (
                <div key={s.label} className="bg-white/5 backdrop-blur-sm px-4 py-4 text-center">
                  <div className="text-amber-400 font-extrabold text-xl leading-none">{s.value}</div>
                  <div className="text-stone-400 text-xs mt-1 leading-snug">{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom scroll cue */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="text-stone-400 text-[11px] tracking-widest uppercase">Browse Parts</span>
        <div className="w-px h-8 bg-gradient-to-b from-stone-400/50 to-transparent animate-pulse" />
      </div>
    </section>
  );
}
